/**
 * AuditHealQuotationsDialog
 *
 * Admin maintenance tool. Scans every saved quotation for the iter-104
 * "per-sqft collapse" bug (where the saved amount was stored as
 * rate × qty instead of rate × sqft × qty). Two-step flow:
 *
 *   1. "Run Audit (dry run)" → POST /admin/quotations/audit-heal?dry_run=true
 *      Shows a per-quotation sample of what would change.
 *   2. "Apply Heal" → POST /admin/quotations/audit-heal?dry_run=false
 *      Persists corrected amounts + subtotal + grand_total.
 *
 * Healing is reversible only via backup — show the warning prominently.
 */
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { api, formatINR } from '../../lib/store';
import { toast } from 'sonner';
import { ShieldAlert, Loader2, CheckCircle2, AlertTriangle, BellRing } from 'lucide-react';

export default function AuditHealQuotationsDialog({ open, onOpenChange }) {
  const [running, setRunning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [applied, setApplied] = useState(false);

  const runAudit = async () => {
    setRunning(true); setApplied(false);
    try {
      const res = await api.auditHealQuotations(true);
      setResult(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Audit failed');
    } finally {
      setRunning(false);
    }
  };

  const applyHeal = async () => {
    if (!window.confirm('This will rewrite amounts on all broken quotations in the database. Make sure you have a recent backup. Proceed?')) return;
    setApplying(true);
    try {
      const res = await api.auditHealQuotations(false);
      setResult(res.data);
      setApplied(true);
      toast.success(`Healed ${res.data.fixed_items} item(s) across ${res.data.broken_quotations} quotation(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Heal failed');
    } finally {
      setApplying(false);
    }
  };

  const reset = () => { setResult(null); setApplied(false); };

  const runNightlyNow = async () => {
    setRunning(true);
    try {
      const res = await api.runNightlyAuditNow();
      const r = res.data;
      const sw = Number(r.stale_walkins || 0);
      const sl = Number(r.stale_leads || 0);
      const bq = Number(r.broken_quotations || 0);
      if (bq + sw + sl === 0) {
        toast.success(`All ${r.scanned} quotations healthy ✅ — no stale follow-ups either.`);
      } else {
        const bits = [];
        if (bq > 0) bits.push(`${r.fixed_items} broken item(s) in ${bq} quotation(s)`);
        if (sw > 0) bits.push(`${sw} overdue walk-in(s)`);
        if (sl > 0) bits.push(`${sl} overdue lead(s)`);
        toast.success(`Notified ${r.notified} admin(s) — ${bits.join(', ')}.`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Watchdog failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset(); }}>
      <DialogContent className="max-w-2xl max-h-[92vh] flex flex-col" data-testid="audit-heal-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-heda-gold" /> Audit & Heal Quotations
          </DialogTitle>
          <p className="text-xs text-heda-sub mt-1">
            Scans every saved quotation for the per-sqft amount-collapse bug. Step 1 runs a dry-run preview; Step 2 writes the corrected amounts.
          </p>
          <div className="mt-2 flex items-center justify-between gap-2 rounded-xl bg-heda-cream px-3 py-2">
            <span className="text-[11.5px] text-heda-sub">
              <BellRing className="mr-1.5 inline w-3 h-3 -mt-0.5 text-heda-gold" />
              Nightly health check at <strong>02:00 IST</strong>: broken amounts + overdue walk-ins / leads. Admins get a single WhatsApp digest if anything needs attention.
            </span>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-8 shrink-0 rounded-full text-[11.5px] text-heda-ink hover:bg-white"
              onClick={runNightlyNow}
              disabled={running || applying}
              data-testid="audit-run-nightly-now-btn"
            >
              Test now
            </Button>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-1 space-y-3">
          {!result && !running && (
            <div className="border border-dashed border-heda-line rounded-xl p-6 text-center text-sm text-heda-sub">
              Click <strong>Run Audit (dry run)</strong> to scan all quotations. Nothing will be written.
            </div>
          )}

          {running && (
            <div className="flex items-center gap-2 text-sm text-heda-sub py-6 justify-center">
              <Loader2 className="w-4 h-4 animate-spin text-heda-gold" /> Scanning the entire quotations collection…
            </div>
          )}

          {result && (
            <>
              {/* Summary chips */}
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                <Stat label="Quotations scanned" value={result.scanned} testId="audit-stat-scanned" />
                <Stat label="Broken quotations" value={result.broken_quotations} accent="amber" testId="audit-stat-broken" />
                <Stat label="Items to fix" value={result.fixed_items} accent="amber" testId="audit-stat-items" />
                <Stat
                  label="Net amount change"
                  value={formatINR(Math.round(result.total_amount_delta))}
                  accent={result.total_amount_delta >= 0 ? 'green' : 'red'}
                  testId="audit-stat-delta"
                />
              </div>

              {result.broken_quotations === 0 ? (
                <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-700 flex items-center gap-2" data-testid="audit-allclean">
                  <CheckCircle2 className="w-4 h-4" /> All quotations are healthy — nothing to heal.
                </div>
              ) : (
                <>
                  {applied ? (
                    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 text-sm text-emerald-700 flex items-center gap-2" data-testid="audit-applied">
                      <CheckCircle2 className="w-4 h-4" /> Heal applied to {result.fixed_items} item(s) across {result.broken_quotations} quotation(s).
                    </div>
                  ) : (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700 flex gap-2">
                      <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="font-semibold">Dry-run preview below.</p>
                        <p className="mt-1">The audit will ONLY heal items where: (a) category is a per-sqft category (Plywood, Veneers, Doors, MDF, WPC/PVC, Blockboard), (b) the saved <code className="bg-amber-50 px-1 rounded-lg">amount</code> = <code className="bg-amber-50 px-1 rounded-lg">rate × qty</code> (the exact &quot;sqft factor dropped&quot; signature), and (c) healing would only INCREASE the amount. Anything else is left for manual review via cart edit. We recommend a database backup before clicking Apply Heal.</p>
                      </div>
                    </div>
                  )}

                  {/* Sample table */}
                  <div className="border border-heda-line rounded-xl overflow-x-auto bg-white">
                    <table className="w-full text-left text-sm">
                      <thead className="heda-thead">
                        <tr>
                          <th className="px-3 sm:px-4 py-2.5 font-semibold text-left">Quotation</th>
                          <th className="px-3 sm:px-4 py-2.5 font-semibold text-left hidden sm:table-cell">Customer</th>
                          <th className="px-3 sm:px-4 py-2.5 font-semibold text-left hidden md:table-cell">Item</th>
                          <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">Before</th>
                          <th className="px-3 sm:px-4 py-2.5 font-semibold text-right">After</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.samples.map((s, i) => (
                          <tr key={i} className="border-t border-heda-line" data-testid={`audit-sample-row-${i}`}>
                            <td className="px-3 sm:px-4 py-2.5 font-mono text-xs">{s.quotation_number || s.quotation_id.slice(0, 8)}</td>
                            <td className="px-3 sm:px-4 py-2.5 hidden sm:table-cell">{s.customer_name || '—'}</td>
                            <td className="px-3 sm:px-4 py-2.5 truncate max-w-[180px] hidden md:table-cell" title={s.item_desc}>{s.item_desc}</td>
                            <td className="px-3 sm:px-4 py-2.5 text-right tabular-nums text-red-700">{formatINR(Math.round(s.before_amount))}</td>
                            <td className="px-3 sm:px-4 py-2.5 text-right tabular-nums text-emerald-700 font-semibold">{formatINR(Math.round(s.after_amount))}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {result.fixed_items > result.samples.length && (
                    <p className="text-[11px] text-heda-mute italic">
                      Showing {result.samples.length} of {result.fixed_items} items. All others will be healed the same way.
                    </p>
                  )}
                </>
              )}
            </>
          )}
        </div>

        <DialogFooter className="flex-shrink-0 pt-3 border-t border-heda-line gap-2">
          <Button className="rounded-full border-heda-line bg-white text-heda-ink shadow-none hover:bg-white hover:border-heda-gold hover:text-heda-gold" variant="outline" onClick={() => onOpenChange(false)} disabled={running || applying} data-testid="audit-close-btn">
            Close
          </Button>
          {!result || applied ? (
            <Button
              onClick={runAudit}
              disabled={running || applying}
              className="rounded-xl bg-heda-olive text-white shadow-none hover:bg-heda-olive-dark"
              data-testid="audit-run-btn"
            >
              {running ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Scanning…</> : 'Run Audit (dry run)'}
            </Button>
          ) : (
            result.broken_quotations > 0 && (
              <Button
                onClick={applyHeal}
                disabled={applying}
                className="rounded-xl bg-heda-ink text-white shadow-none hover:bg-black"
                data-testid="audit-apply-btn"
              >
                {applying ? <><Loader2 className="w-3 h-3 mr-2 animate-spin" /> Healing…</> : `Apply Heal (${result.fixed_items} items)`}
              </Button>
            )
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Stat({ label, value, accent, testId }) {
  const colours = {
    amber: 'bg-amber-50 border-amber-200 text-amber-700',
    green: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    red: 'bg-red-50 border-red-200 text-red-700',
  };
  const cls = colours[accent] || 'bg-heda-cream border-heda-line text-heda-ink';
  return (
    <div className={`rounded-xl border px-3 py-2 ${cls}`} data-testid={testId}>
      <p className="text-[10.5px] font-semibold uppercase tracking-wider opacity-80">{label}</p>
      <p className="font-display text-xl font-bold tabular-nums mt-0.5">{value}</p>
    </div>
  );
}
